import React, { useState, useEffect, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { loadMoreProducts } from '../../../actions';
import useIsInViewport from '../../../hooks/useIsInViewport';

const LoadMore = ({
    // Browsing (category) props from redux
    activeSection,
    activeHandle,
    afterCursor,
    currentProducts,
    currentFilters,
    dispatch,
    hasNextPage,
    loadingMore = false,
    skintone,
    // Shared
    scrollContainer,
    // Search mode override props
    isSearchMode = false,
    onLoadMoreSearch,
    searchHasNextPage,
    searchAfterCursor,
    searchLoading = false,
}) => {
    const [fullyScrolled, setFullyScrolled] = useState(false);
    const buttonRef = useRef(null);
    const isInViewport = useIsInViewport(buttonRef);
    const wasAtBottomRef = useRef(false);
    const requestInFlightRef = useRef(false);
    const prevScrollHeightRef = useRef(0);
    const prevScrollTopRef = useRef(0);
    const BOTTOM_THRESHOLD = 10; // px tolerance near bottom before triggering
    const debounceRef = useRef(null);
    const DEBOUNCE_MS = 500; // delay between automatic load triggers

    useEffect(() => {
        const container = scrollContainer.current;
        if (!container) return;
        const onScroll = () => {
            if (container.scrollTop + container.clientHeight >= container.scrollHeight - BOTTOM_THRESHOLD) {
                setFullyScrolled(true);
            } else if (fullyScrolled) {
                setFullyScrolled(false);
            }
        };
        container.addEventListener('scroll', onScroll);
        onScroll();
        return () => container.removeEventListener('scroll', onScroll);
    }, [scrollContainer, fullyScrolled]);

    // Determine effective pagination & loading state (search vs browse)
    const effectiveHasNextPage = isSearchMode ? searchHasNextPage : hasNextPage;
    const effectiveAfterCursor = isSearchMode ? searchAfterCursor : afterCursor;
    const effectiveLoading = isSearchMode ? searchLoading : loadingMore; // use granular loading state for UI

    const triggerLoad = useCallback(() => {
        if (requestInFlightRef.current || effectiveLoading || !effectiveHasNextPage) return;
        requestInFlightRef.current = true;
        const container = scrollContainer.current;
        if (container) {
            const atBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - BOTTOM_THRESHOLD;
            wasAtBottomRef.current = atBottom;
            prevScrollHeightRef.current = container.scrollHeight;
            prevScrollTopRef.current = container.scrollTop;
        }
        if (isSearchMode) {
            // Delegate to search callback (expects to manage its own loading state flags)
            if (typeof onLoadMoreSearch === 'function') {
                onLoadMoreSearch(effectiveAfterCursor);
            }
        } else {
            dispatch(
                loadMoreProducts(
                    activeSection,
                    activeHandle,
                    effectiveAfterCursor,
                    currentProducts,
                    currentFilters,
                    skintone
                )
            );
        }
        setFullyScrolled(false);
    }, [
        dispatch,
        activeSection,
        activeHandle,
        currentProducts,
        currentFilters,
        skintone,
        scrollContainer,
        effectiveHasNextPage,
        effectiveAfterCursor,
        effectiveLoading,
        isSearchMode,
        onLoadMoreSearch,
    ]);

    useEffect(() => {
        if (!buttonRef.current) return;
        if (effectiveLoading || requestInFlightRef.current) return;
        const display = window.getComputedStyle(buttonRef.current).display;
        const shouldTrigger = isInViewport || (fullyScrolled && display !== 'none');
        if (shouldTrigger && !debounceRef.current) {
            debounceRef.current = setTimeout(() => {
                triggerLoad();
                debounceRef.current = null;
            }, DEBOUNCE_MS);
        }
    }, [isInViewport, fullyScrolled, triggerLoad, effectiveLoading]);

    // After load completes: restore previous scroll context if we were at bottom
    useEffect(() => {
        if (!effectiveLoading) {
            const container = scrollContainer.current;
            if (wasAtBottomRef.current && container) {
                const delta = container.scrollHeight - prevScrollHeightRef.current;
                if (delta > 0) {
                    container.scrollTop = prevScrollTopRef.current;
                }
            }
            wasAtBottomRef.current = false;
            requestInFlightRef.current = false;
            setFullyScrolled(false);
        }
    }, [effectiveLoading, scrollContainer]);

    useEffect(
        () => () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        },
        []
    );

    if (!effectiveHasNextPage) {
        return (
            <div className="load-more">
                <small>End of products</small>
                <button ref={buttonRef} style={{ display: 'none' }} />
            </div>
        );
    }

    return (
        <div className="load-more-inline" style={{ textAlign: 'center', padding: '1rem' }}>
            {effectiveLoading ? (
                <span>Loading products...</span>
            ) : (
                <button
                    ref={buttonRef}
                    className="btn btn-pill load-more"
                    onClick={() => {
                        if (debounceRef.current) {
                            clearTimeout(debounceRef.current);
                            debounceRef.current = null;
                        }
                        triggerLoad();
                    }}
                    aria-label={isSearchMode ? 'Load more search results' : 'Load more products'}
                >
                    {isSearchMode ? 'Load more results' : 'Load more'}
                </button>
            )}
        </div>
    );
};

const mapStateToProps = (state) => ({
    activeSection: state.navigation.selected,
    activeHandle: state.browser.selectedCategory.handle,
    afterCursor: state.paginationInfo.afterCursor,
    hasNextPage: state.paginationInfo.hasNextPage,
    currentProducts: state.browser.products,
    currentFilters: state.currentFilters,
    loadingMore: state.loadingMore,
    skintone: state.builds[state.currentBuildIndex].skintone,
});

LoadMore.propTypes = {
    activeSection: PropTypes.string.isRequired,
    activeHandle: PropTypes.string.isRequired,
    afterCursor: PropTypes.string,
    currentProducts: PropTypes.array.isRequired,
    currentFilters: PropTypes.object,
    dispatch: PropTypes.func.isRequired,
    hasNextPage: PropTypes.bool.isRequired,
    loadingMore: PropTypes.bool,
    scrollContainer: PropTypes.shape({ current: PropTypes.any }),
    skintone: PropTypes.string,
    // Search overrides
    isSearchMode: PropTypes.bool,
    onLoadMoreSearch: PropTypes.func,
    searchHasNextPage: PropTypes.bool,
    searchAfterCursor: PropTypes.string,
    searchLoading: PropTypes.bool,
};

export default connect(mapStateToProps)(LoadMore);
